/**
 * Cache Service for ARMS Frontend
 * Provides in-memory and localStorage caching with TTL support
 */

// Default cache TTL values (in milliseconds)
const DEFAULT_CACHE_TTL = {
  projects: 5 * 60 * 1000,     // 5 minutes for projects
  runs: 3 * 60 * 1000,        // 3 minutes for runs 
  projectDetails: 10 * 60 * 1000, // 10 minutes for project details
  organizations: 15 * 60 * 1000,  // 15 minutes for organizations
  apiKeys: 30 * 60 * 1000,    // 30 minutes for API keys
};

class CacheService {
  constructor() {
    this.memoryCache = new Map();
    this.cachePrefix = 'arms_cache_';
  }

  /**
   * Generate cache key based on API endpoint and user ID
   */
  generateCacheKey(endpoint, userId, params = {}) {
    const paramString = Object.keys(params).length > 0 
      ? '_' + JSON.stringify(params) 
      : '';
    return `${this.cachePrefix}${endpoint}_${userId}${paramString}`;
  }

  /**
   * Set data in both memory and localStorage cache
   */
  set(key, data, ttl = DEFAULT_CACHE_TTL.projects) {
    const expiresAt = Date.now() + ttl;
    const cacheItem = {
      data,
      expiresAt,
      timestamp: Date.now()
    };

    // Store in memory cache (always works)
    this.memoryCache.set(key, cacheItem);

    // Store in localStorage cache (with size check)
    try {
      const serialized = JSON.stringify(cacheItem);
      const sizeInMB = new Blob([serialized]).size / (1024 * 1024);
      
      // Only store in localStorage if < 2MB to avoid quota issues
      if (sizeInMB < 2) {
        localStorage.setItem(key, serialized);
        console.log(`💾 Cached to localStorage: ${key} (${sizeInMB.toFixed(2)} MB)`);
      } else {
        console.warn(`⚠️ Data too large for localStorage (${sizeInMB.toFixed(2)} MB), using memory cache only: ${key}`);
      }
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ localStorage quota exceeded, clearing old cache entries...');
        // Try to clear some old cache entries
        this.clearOldLocalStorageEntries();
      } else {
        console.warn('Failed to store in localStorage:', error);
      }
    }
  }
  
  /**
   * Clear old localStorage entries to free up space
   */
  clearOldLocalStorageEntries() {
    try {
      const cacheEntries = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cachePrefix)) {
          try {
            const item = JSON.parse(localStorage.getItem(key));
            cacheEntries.push({ key, timestamp: item.timestamp || 0 });
          } catch (e) {
            // Remove invalid entries
            localStorage.removeItem(key);
          }
        }
      }
      
      // Sort by timestamp (oldest first) and remove oldest 50%
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = cacheEntries.slice(0, Math.ceil(cacheEntries.length / 2));
      toRemove.forEach(entry => {
        localStorage.removeItem(entry.key);
        console.log(`🗑️ Removed old cache entry: ${entry.key}`);
      });
    } catch (error) {
      console.error('Error clearing old localStorage entries:', error);
    }
  }

  /**
   * Get data from cache (memory first, then localStorage)
   */
  get(key) {
    // Check memory cache first
    if (this.memoryCache.has(key)) {
      const item = this.memoryCache.get(key);
      if (item.expiresAt > Date.now()) {
        return item.data;
      } else {
        this.memoryCache.delete(key);
      }
    }

    // Check localStorage cache
    try {
      const storedItem = localStorage.getItem(key);
      if (storedItem) {
        const item = JSON.parse(storedItem);
        if (item.expiresAt > Date.now()) {
          // Restore to memory cache
          this.memoryCache.set(key, item);
          return item.data;
        } else {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return null;
  }

  /**
   * Check if data exists in cache and is not expired
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Remove specific cache entry
   */
  remove(key) {
    this.memoryCache.delete(key);
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  }

  /**
   * Clear all cache for a specific user
   */
  clearUserCache(userId) {
    // Clear memory cache
    for (const [key] of this.memoryCache) {
      if (key.includes(`_${userId}`)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear localStorage cache
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cachePrefix) && key.includes(`_${userId}`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Clear all cache data
   */
  clearAll() {
    this.memoryCache.clear();
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cachePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const memoryStats = {
      size: this.memoryCache.size,
      keys: Array.from(this.memoryCache.keys())
    };

    let localStorageStats = { size: 0, keys: [] };
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cachePrefix)) {
          localStorageStats.size++;
          localStorageStats.keys.push(key);
        }
      }
    } catch (error) {
      console.warn('Failed to get localStorage stats:', error);
    }

    return {
      memory: memoryStats,
      localStorage: localStorageStats
    };
  }

  /**
   * Cache-enabled API wrapper
   */
  async cachedApiCall(
    cacheKey, 
    apiFunction, 
    ttl = DEFAULT_CACHE_TTL.projects,
    forceRefresh = false
  ) {
    // Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cachedData = this.get(cacheKey);
      if (cachedData) {
        console.log(`📋 Cache hit for: ${cacheKey}`);
        return cachedData;
      }
    }

    console.log(`🌐 Cache miss, fetching: ${cacheKey}`);
    
    try {
      // Call the actual API function
      const freshData = await apiFunction();
      
      // Cache the result
      this.set(cacheKey, freshData, ttl);
      
      return freshData;
    } catch (error) {
      // If API fails but we have expired cache, return it as fallback
      const expiredData = this.getExpiredData(cacheKey);
      if (expiredData) {
        console.warn(`⚠️ API failed, using expired cache for: ${cacheKey}`);
        return expiredData;
      }
      throw error;
    }
  }

  /**
   * Get expired data (ignoring TTL) - useful for fallback scenarios
   */
  getExpiredData(key) {
    // Check memory cache
    if (this.memoryCache.has(key)) {
      return this.memoryCache.get(key).data;
    }

    // Check localStorage
    try {
      const storedItem = localStorage.getItem(key);
      if (storedItem) {
        const item = JSON.parse(storedItem);
        return item.data;
      }
    } catch (error) {
      console.warn('Failed to read expired data from localStorage:', error);
    }

    return null;
  }

  /**
   * Invalidate cache entries that match a pattern
   */
  invalidatePattern(pattern) {
    // Invalidate memory cache
    for (const [key] of this.memoryCache) {
      if (key.includes(pattern)) {
        this.memoryCache.delete(key);
      }
    }

    // Invalidate localStorage cache
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cachePrefix) && key.includes(pattern)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to invalidate localStorage pattern:', error);
    }
  }
}

// Export singleton instance
export const cacheService = new CacheService();

// Export cache TTL constants for use in components
export { DEFAULT_CACHE_TTL };

// Export cache key generators for consistency
export const CACHE_KEYS = {
  PROJECTS: (userId) => `projects_${userId}`,
  RUNS: (userId) => `runs_${userId}`,
  PROJECT_DETAILS: (projectId, userId) => `project_details_${projectId}_${userId}`,
  ORGANIZATIONS: (userId) => `organizations_${userId}`,
  API_KEYS: (userId) => `api_keys_${userId}`
}; 